import { NextPage } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { useLoadAnalytics } from '@sy/app-client/src/ssrAdapter/services/analytics';
import { CacheCaseStudy } from '@sy/ssr-types';

import MarketingTemplate from '~/components/templates/MarketingTemplate';
import { buildGetServerSideProps, translate } from '~/getServerSideProps';
import type { CommonContext, InferProps } from '~/getServerSideProps';
import CaseStudyPage from '~/scenes/CaseStudy/scenes/CaseStudyPage';
import { CaseStudiesOverview } from '~/scenes/CaseStudies';
import { getCaseStudies, getCaseStudy } from '~/services/caseStudy';
import { CaseStudyTag } from '~/services/caseStudy/caseStudyTags';
import buildMetadata, {
	buildMetadataFromSeoMetaTags,
} from '~/utils/buildMetadata';
import { getMarketingNavigation } from '~/utils/getNavigation';

const CaseStudy: NextPage<InferProps<typeof getServerSideProps>> = ({
	caseStudies,
	data,
	featureFlags,
	navigation,
	tag,
	user,
}) => {
	useLoadAnalytics();

	if (caseStudies) {
		return (
			<MarketingTemplate
				activePage="/case-studies"
				featureFlags={featureFlags}
				navigation={navigation}
				user={user}
				topPadding={false}
			>
				<CaseStudiesOverview caseStudies={caseStudies} tag={tag} />
			</MarketingTemplate>
		);
	}

	return (
		<MarketingTemplate
			activePage={data.urlPath}
			featureFlags={featureFlags}
			navigation={navigation}
			user={user}
		>
			<CaseStudyPage data={data} />
		</MarketingTemplate>
	);
};

interface UrlParams extends ParsedUrlQuery {
	slug?: string;
	tag?: string;
}

const customServerSideProps = async (context: CommonContext<UrlParams>) => {
	const { featureFlags, locale, query, params, req } = context;

	const { slug } = params || {};
	const isPreview = !!query?.preview;

	// Load case studies grid
	if (!slug) {
		const tag = query.tag ? (query.tag as CaseStudyTag) : null;
		return {
			props: {
				caseStudies: await getCaseStudies({
					isPreview,
					tag,
					locale: isPreview ? locale : 'en',
				}),
				metadata: buildMetadata({
					title: translate(locale, 'common.title.caseStudies'),
				}),
				navigation: await getMarketingNavigation({
					featureFlags,
					locale,
					query,
				}),
				tag,
			},
		};
	}

	// Load specific case study
	const [data, navigation] = await Promise.all([
		getCaseStudy({
			slug,
			isPreview,
			locale: isPreview ? locale : 'en',
		}),
		getMarketingNavigation({ featureFlags, locale, query }),
	]);

	if (!data) {
		return {
			notFound: true,
		};
	}

	const host = req.headers.host as string;

	return {
		props: {
			data,
			metadata: buildMetadataFromSeoMetaTags({
				host,
				locale,
				slug: data.slug,
				seoMetaTags: data.seoMetaTags,
			}),
			navigation,
		},
	};
};

export default CaseStudy;

export const getServerSideProps = buildGetServerSideProps<
	| {
			tag?: CaseStudyTag | null;
			caseStudies: CacheCaseStudy[] | null;
	  }
	| {
			data: CacheCaseStudy;
	  }
>({
	customServerSideProps,
	translationNamespaces: [
		'caseStudies',
		'common',
		'auth',
		'landing',
		'landingMainPage',
		'pricing',
	],
});
